import React from 'react';
import useSWR from 'swr';
import { useApi } from '../hooks/useApi';
import { Box, CircularProgress, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, alpha } from '@mui/material';
import { cyber } from '../theme';

// Styling for technical MITRE identifiers (e.g. T1059, T1059.001, TA0002)
const MITRE_ID_REGEX = /^T(A?)\d{4}(\.\d{3})?$/;
const monoIdSx = {
    fontFamily: cyber.fontMono,
    fontSize: '0.8rem',
    color: alpha(cyber.accent.cyan, 0.9),
    whiteSpace: 'nowrap',
} as const;

interface TTPsTableProps {
    tableUrl?: string;
}

const parseMarkdownTable = (markdown: string): Array<Record<string, string>> => {
    if (!markdown) return [];
    const lines = markdown.trim().split('\n');
    if (lines.length < 2) return [];
    const headers = lines[0].split('|').map(h => h.trim()).filter(Boolean);
    const rows = lines.slice(2).map(line => {
        const values = line.split('|').map(v => v.trim()).filter(Boolean);
        const rowObject: { [key: string]: string } = {};
        headers.forEach((header, index) => {
            rowObject[header] = values[index] || '';
        });
        return rowObject;
    });
    return rows;
};

const TTPsTable: React.FC<TTPsTableProps> = ({ tableUrl }) => {
    // FIX: Use the correct fetcher for simple text content
    const { simpleTextFetcher } = useApi();
    
    const { data: tableData, error, isLoading } = useSWR(
        tableUrl,
        simpleTextFetcher // Use the correct fetcher here
    );

    if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>;
    if (error) return <Alert severity="error">Could not load TTPs table.</Alert>;

    const ttps = parseMarkdownTable(tableData || '');

    return (
        <Box>
            {ttps.length > 0 ? (
                <TableContainer component={Paper}>
                    <Table stickyHeader aria-label="ttps table">
                        <TableHead>
                            <TableRow>
                                {Object.keys(ttps[0]).map(header => <TableCell key={header}>{header}</TableCell>)}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {ttps.map((row, index) => (
                                <TableRow key={index}>
                                    {Object.values(row).map((cell, cellIndex) => (
                                        <TableCell key={cellIndex} sx={MITRE_ID_REGEX.test(cell) ? monoIdSx : undefined}>
                                            {cell}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Alert severity="info">No TTPs table data found.</Alert>
            )}
        </Box>
    );
};

export default TTPsTable;